import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

const MobileStandingsTable = ({ standings }) => {
  const [selectedDivision, setSelectedDivision] = useState('all');

  const getDivisions = () => {
    const divisions = {};
    
    // Flatten the standings array if it's nested
    const flatStandings = Array.isArray(standings[0]) ? standings[0] : standings;
    
    // Filter for MLB teams only (league.id === 1)
    const mlbStandings = flatStandings.filter(entry => 
      entry.league?.id === 1 || entry.league?.name === 'MLB'
    );
    
    mlbStandings.forEach(entry => {
      const division = entry.group?.name || 'League';
      if (!divisions[division]) {
        divisions[division] = [];
      }
      divisions[division].push(entry);
    });
    return divisions;
  };

  const getPositionColor = (position) => {
    if (position <= 1) return 'bg-gradient-to-r from-yellow-500 to-amber-600 text-white';
    if (position <= 3) return 'bg-gradient-to-r from-blue-500 to-blue-600 text-white';
    return 'bg-gray-700 text-gray-300';
  };

  const getStreakIcon = (form) => {
    if (!form || form.length === 0) return null;
    const lastGame = form[form.length - 1];
    if (lastGame === 'W') return <TrendingUp className="w-3 h-3 text-green-400" />;
    if (lastGame === 'L') return <TrendingDown className="w-3 h-3 text-red-400" />;
    return <Minus className="w-3 h-3 text-gray-400" />;
  };

  const divisions = getDivisions();
  const divisionKeys = Object.keys(divisions);

  // Get teams to display based on selected division
  const teamsToDisplay = useMemo(() => {
    if (selectedDivision === 'all') {
      return Object.entries(divisions);
    }
    return [[selectedDivision, divisions[selectedDivision] || []]];
  }, [selectedDivision, divisions]);

  if (!standings || standings.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        No standings data available
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Division Selector - Horizontal scroll on mobile */}
      <div className="overflow-x-auto pb-2">
        <div className="flex gap-2 min-w-max">
          <button
            onClick={() => setSelectedDivision('all')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
              selectedDivision === 'all'
                ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
                : 'bg-gray-800/50 text-gray-400'
            }`}
          >
            All Divisions
          </button>
          {divisionKeys.map(division => (
            <button
              key={division}
              onClick={() => setSelectedDivision(division)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                selectedDivision === division
                  ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
                  : 'bg-gray-800/50 text-gray-400'
              }`}
            >
              {division}
            </button>
          ))}
        </div>
      </div>

      {teamsToDisplay.map(([division, teams]) => (
        <motion.div
          key={division}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-4 border border-gray-700/50"
        >
          <h3 className="text-lg font-bold mb-3 bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent">
            {division}
          </h3>
          
          {/* Mobile-optimized table */}
          <div className="overflow-x-auto -mx-4 px-4">
            <table className="w-full min-w-[320px]">
              <thead>
                <tr className="text-xs text-gray-400">
                  <th className="text-left pb-2 pr-2">#</th>
                  <th className="text-left pb-2 pr-2">Team</th>
                  <th className="text-center pb-2 px-2">W</th>
                  <th className="text-center pb-2 px-2">L</th>
                  <th className="text-center pb-2 px-2">PCT</th>
                  <th className="text-center pb-2 px-2">GB</th>
                  <th className="text-center pb-2 pl-2">Home</th>
                </tr>
              </thead>
              <tbody>
                {teams.sort((a, b) => a.position - b.position).map((entry, index) => {
                  const team = entry.team;
                  const wins = entry.games?.win?.total || 0;
                  const losses = entry.games?.lose?.total || 0;
                  const winPct = entry.games?.win?.percentage || '0.000';
                  const gamesBack = entry.games?.behind || '-';
                  const homeRecord = entry.home ? `${entry.home.win || 0}-${entry.home.lose || 0}` : '-';
                  
                  return (
                    <motion.tr
                      key={team?.id || `${division}-${index}`}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.03 }}
                      className="border-t border-gray-700/30"
                    >
                      <td className="py-2 pr-2">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${getPositionColor(entry.position)}`}>
                          {entry.position}
                        </div>
                      </td>
                      <td className="py-2 pr-2">
                        <div className="flex items-center gap-2">
                          {team?.logo && (
                            <img 
                              src={team.logo} 
                              alt={team.name}
                              className="w-6 h-6 object-contain flex-shrink-0"
                            />
                          )}
                          <div className="min-w-0">
                            <div className="font-medium text-sm text-white truncate">
                              {team?.name || 'Unknown'}
                            </div>
                            {/* Show streak below team name on mobile */}
                            <div className="flex items-center gap-1 text-xs">
                              {getStreakIcon(entry.form)}
                              <span className="text-gray-400">
                                {entry.form ? `${entry.form[entry.form.length - 1]}${entry.streak || ''}` : '-'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="text-center py-2 px-2 text-sm font-medium text-white">
                        {wins}
                      </td>
                      <td className="text-center py-2 px-2 text-sm text-gray-400">
                        {losses}
                      </td>
                      <td className="text-center py-2 px-2 text-sm font-medium text-blue-400">
                        {winPct}
                      </td>
                      <td className="text-center py-2 px-2 text-sm text-gray-400">
                        {gamesBack}
                      </td>
                      <td className="text-center py-2 pl-2 text-xs text-gray-400">
                        {homeRecord}
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

export default MobileStandingsTable;